// src/admin/components/RunFilters.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import FilterChipGroup from './FilterChipGroup.jsx';
import DateRangePicker from './DateRangePicker.jsx';
import Disclosure from './Disclosure.jsx';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed',    label: 'Failed' },
];

// Valid platform · content_format pairs (mirrors resolvePlatformAndFormat in
// backendClient.js). A single combined dropdown prevents impossible combos
// like LinkedIn + SEO. Selecting an option emits BOTH platform and
// content_format together; "Tutti" clears both. `cf: ''` means platform-only
// (e.g. Ads, whose content_format is the channel and varies per run).
const COMBO_OPTIONS = [
  { platform: 'linkedin',  cf: 'post',          label: 'LinkedIn · Post' },
  { platform: 'linkedin',  cf: 'carousel',      label: 'LinkedIn · Carousel' },
  { platform: 'linkedin',  cf: 'newsletter',    label: 'LinkedIn · Newsletter' },
  { platform: 'blog',      cf: 'seo',           label: 'Blog · SEO Article' },
  { platform: 'blog',      cf: 'case-study',    label: 'Blog · Case Study' },
  { platform: 'email',     cf: 'email-classic', label: 'Email · Classic' },
  { platform: 'landing',   cf: 'landing',       label: 'Landing Page' },
  { platform: 'instagram', cf: 'post',          label: 'Instagram · Post' },
  { platform: 'facebook',  cf: 'post',          label: 'Facebook · Post' },
  { platform: 'ads',       cf: '',              label: 'Ads' },
];

const comboKey = (platform, cf) => (platform ? `${platform}|${cf || ''}` : '');

const labelStyle  = { fontSize: '12px', color: COLORS.textSecondary, marginBottom: '6px' };
const selectStyle = { padding: '6px 10px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, fontSize: '13px', fontFamily: FONT_SANS };
const inputStyle  = { padding: '6px 10px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, fontSize: '13px', fontFamily: FONT_SANS, minWidth: '220px' };

export default function RunFilters({
  status = [], since = '', before = '',
  user = '', platform = '', contentFormat = '',
  onChange,
}) {
  const { t } = useTranslation('admin');
  const selectedKey = comboKey(platform, contentFormat);
  const selectValue = COMBO_OPTIONS.some(o => comboKey(o.platform, o.cf) === selectedKey)
    ? selectedKey
    : '';

  function onComboChange(e) {
    const combo = COMBO_OPTIONS.find(o => comboKey(o.platform, o.cf) === e.target.value);
    onChange({ platform: combo?.platform || '', content_format: combo?.cf || '' });
  }

  return (
    <>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
        <div>
          <div style={labelStyle}>{t('runFilters.status')}</div>
          <FilterChipGroup
            options={STATUS_OPTIONS}
            value={status}
            onChange={(next) => onChange({ status: next.join(',') })}
          />
        </div>
        <div>
          <div style={labelStyle}>{t('runFilters.period')}</div>
          <DateRangePicker since={since} before={before} onChange={(patch) => onChange(patch)} />
        </div>
      </div>

      <Disclosure title={t('runFilters.moreFilters')}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={labelStyle}>{t('runFilters.userEmail')}</div>
            <input
              type="text" placeholder="user@example.com"
              value={user}
              onChange={e => onChange({ user: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>{t('runFilters.platformFormat')}</div>
            <select value={selectValue} onChange={onComboChange} style={selectStyle}>
              <option value="">{t('runFilters.all')}</option>
              {COMBO_OPTIONS.map(opt => (
                <option key={comboKey(opt.platform, opt.cf)} value={comboKey(opt.platform, opt.cf)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Disclosure>
    </>
  );
}
